// Editor command dispatcher. Domain logic lives in `exec/*.ts`; this file only
// orchestrates: maps each EditorCommand / InternalEditorAction to its executor,
// and applies cross-domain side effects (auto-fork built-in libraries,
// auto-detach edited component slots) after structure-changing edits.

import type { CommandExecution } from './commandHistory.js';
import type { EditorCommand } from './commandBuilders.js';
import type { AppState } from '../state/appState.js';
import {
  isGraphStructureEdit,
  findSceneTargetByNodeIdFor,
  nodeTypeByIdFor,
  unreachableEditorCommand,
} from './exec/shared.js';
import {
  type AddEdgeAction,
  type DeleteParamValueAction,
  type DropNodeAction,
  type RemoveEdgeAction,
  type RestoreNodeAction,
  execAddEdgeFor,
  execConnectFor,
  execCreateNodeFor,
  execCutEdgesFor,
  execDeleteNodeFor,
  execDeleteParamValueFor,
  execDropNodeFor,
  execMoveNodeFor,
  execReconnectFor,
  execRemoveEdgeFor,
  execRestoreNodeFor,
  execReplaceNodeFor,
  execSetDataFor,
  execSetParamValueFor,
} from './exec/graph.js';
import type {
  DropInstanceAction,
  DropLayerAction,
  DropOwnedInstanceAction,
  RemoveFrameAction,
} from './exec/sceneRemoval.js';
import type {
  RestoreComponentAction,
  RestoreLayerDefAction,
  RestoreSceneSubtreeAction,
} from './exec/sceneRestore.js';
import {
  execAddExistingInstanceFor,
  execAddFrameFor,
  execAddInstanceFor,
  execAddLayerFor,
  execClearFor,
  execDeleteSceneNodeFor,
  execDropInstanceFor,
  execDropLayerFor,
  execDropOwnedInstanceFor,
  execRemoveFrameFor,
  execRenameComponentFor,
  execRenameSceneNodeFor,
  execRestoreComponentFor,
  execRestoreLayerDefFor,
  execRestoreSceneSubtreeFor,
  execSetFramePropertyFor,
  execSetInstancePropertyFor,
  execSetNodeParamOverrideFor,
} from './exec/scene.js';
import type { ClearHooks } from './exec/app.js';
import type { SceneRemovalHooks } from './exec/sceneRemoval.js';
import {
  type RestoreLibraryItemAction,
  execCreateLibraryItemFor,
  execDeleteLibraryItemFor,
  execRestoreLibraryItemFor,
  execSaveToLibraryFor,
} from './exec/library.js';
import {
  type SetComponentSlotAction,
  autoDetachEditedComponentSlotIfNeededFor,
  autoForkEditedBuiltInLibraryIfNeededFor,
  editorAutoDetachComponentSlotHooks,
  execAttachLibraryFor,
  execDetachLibraryFor,
  execSetComponentSlotFor,
} from './exec/componentSlot.js';
import type { ComponentSlotHooks } from './exec/componentSlot.js';
import type { AutoDetachComponentSlotHooks } from './exec/componentSlotAutoDetach.js';
import type { AutoForkBuiltInLibraryHooks } from './exec/componentSlotAutoFork.js';
import {
  type EditorSessionState,
  type RestoreEditorSessionAction,
  execRestoreEditorSessionFor,
} from './exec/session.js';
import type { RestoreEditorSessionHooks } from './exec/session.js';
import { execSetRuntimeGraphFor, execSetDataOwnerFor, execSetDataSourcesFor, execSetShapesFor, execSetRuntimeModelFor, execSetRuntimeStateFor, execSetSceneEventsFor, execSetSceneDataContextFor } from './exec/data.js';

export type {
  EditorSessionState,
  DropNodeAction,
  RestoreNodeAction,
  AddEdgeAction,
  RemoveEdgeAction,
  DeleteParamValueAction,
  RemoveFrameAction,
  DropOwnedInstanceAction,
  DropLayerAction,
  DropInstanceAction,
  RestoreSceneSubtreeAction,
  RestoreComponentAction,
  RestoreLayerDefAction,
  RestoreEditorSessionAction,
  RestoreLibraryItemAction,
  SetComponentSlotAction,
};

export interface ActionBase<TType extends string = string> {
  type: TType;
}

export type InternalEditorAction =
  | DropNodeAction
  | RestoreNodeAction
  | AddEdgeAction
  | RemoveEdgeAction
  | DeleteParamValueAction
  | RemoveFrameAction
  | DropInstanceAction
  | DropOwnedInstanceAction
  | DropLayerAction
  | RestoreSceneSubtreeAction
  | RestoreComponentAction
  | RestoreLayerDefAction
  | RestoreEditorSessionAction
  | RestoreLibraryItemAction
  | SetComponentSlotAction;

export type EditorAction = ActionBase & (EditorCommand | InternalEditorAction);

export type ActionExecution<T = unknown> = CommandExecution<T, EditorAction>;

export interface EditorCommandExecutorHooks {
  clear?: ClearHooks;
  sceneRemoval?: SceneRemovalHooks;
  componentSlot?: ComponentSlotHooks;
  autoDetachComponentSlot?: AutoDetachComponentSlotHooks;
  autoForkBuiltInLibrary?: AutoForkBuiltInLibraryHooks;
  editorSession?: RestoreEditorSessionHooks;
}

export interface EditorCommandExecutor {
  executeCommand(appState: AppState, cmd: EditorCommand): ActionExecution;
  executeInternalAction(appState: AppState, action: InternalEditorAction): ActionExecution;
}

export function editorCommandLabelFor(appState: AppState, cmd: EditorCommand): string {
  switch (cmd.type) {
    case 'graph:createNode':
      return `Add node: ${cmd.node.type}`;
    case 'graph:deleteNode':
      return `Delete node: ${nodeTypeByIdFor(appState, cmd.nodeId)}`;
    case 'graph:moveNode':
      return `Move node: ${nodeTypeByIdFor(appState, cmd.nodeId)}`;
    case 'graph:setData':
      return `Edit ${nodeTypeByIdFor(appState, cmd.nodeId)}: ${cmd.field}`;
    case 'graph:replaceNode':
      return `Convert node: ${nodeTypeByIdFor(appState, cmd.nodeId)} to ${cmd.node.type}`;
    case 'graph:connect': {
      const prefix = cmd.isReconnect ? 'Reconnect' : 'Connect';
      return `${prefix} nodes: ${nodeTypeByIdFor(appState, cmd.edge.fromNode)} -> ${nodeTypeByIdFor(appState, cmd.edge.toNode)}`;
    }
    case 'graph:reconnect':
      return `Reconnect nodes: ${nodeTypeByIdFor(appState, cmd.toEdge.fromNode)} -> ${nodeTypeByIdFor(appState, cmd.toEdge.toNode)}`;
    case 'graph:cutEdges':
      return cmd.edges.length === 1
        ? `Disconnect nodes: ${nodeTypeByIdFor(appState, cmd.edges[0].fromNode)} -> ${nodeTypeByIdFor(appState, cmd.edges[0].toNode)}`
        : `Cut ${cmd.edges.length} connections`;
    case 'params:setValue':
      return 'Adjust parameter';
    case 'scene:addFrame':
      return 'Add frame';
    case 'scene:addLayer':
      return 'Add layer';
    case 'scene:addInstance':
      return 'Add instance';
    case 'scene:addExistingInstance':
      return 'Add component instance';
    case 'scene:deleteNode':
      return 'Delete scene node';
    case 'scene:setFrameProperty':
    case 'scene:setInstanceProperty':
      return `Set ${cmd.prop}`;
    case 'scene:setNodeParamOverride':
      return cmd.value === undefined ? 'Clear override' : 'Set override';
    case 'scene:rename': {
      const node = findSceneTargetByNodeIdFor(appState, cmd.nodeId)?.scene.nodes[cmd.nodeId];
      if (node?.kind === 'instance') return 'Rename instance';
      if (node?.kind === 'layer') return 'Rename layer';
      return 'Rename frame';
    }
    case 'component:rename':
      return 'Rename component';
    case 'app:clear':
      return 'Clear graph';
    case 'library:create':
      return `Create ${cmd.kind} library item`;
    case 'library:save':
      return `Save to ${cmd.kind} library`;
    case 'library:delete':
      return `Delete ${cmd.kind} library item`;
    case 'library:attach':
      return `Attach ${cmd.slot} from library`;
    case 'library:detach':
      return `Detach ${cmd.slot} to inline`;
    case 'runtime:setGraph': {
      const graphLabel = cmd.kind === 'data' ? 'Data Graph' : 'Event Graph';
      return cmd.graph ? `Update ${graphLabel}: ${cmd.graphId}` : `Delete ${graphLabel}: ${cmd.graphId}`;
    }
    case 'data:setSources': return 'Update Data Sources';
    case 'assets:setShapes': return 'Update Shapes';
    case 'data:setOwner':
      return cmd.owner.type === 'document' ? 'Set global Data Graph' : 'Set scene behavior';
    case 'runtime:setState': return 'Update Runtime State';
    case 'runtime:setModel': return 'Rename Runtime State';
    case 'event:setSceneEvents': return 'Set scene events';
    case 'data:setSceneContext': return 'Set scene data context';
  }
  return unreachableEditorCommand(cmd);
}

function executeActionFor(
  appState: AppState,
  action: EditorAction,
  hooks: EditorCommandExecutorHooks = {},
): ActionExecution {
  switch (action.type) {
    case 'graph:createNode': return execCreateNodeFor(appState, action);
    case 'graph:dropNode': return execDropNodeFor(appState, action);
    case 'graph:deleteNode': return execDeleteNodeFor(appState, action);
    case 'graph:restoreNode': return execRestoreNodeFor(appState, action);
    case 'graph:moveNode': return execMoveNodeFor(appState, action);
    case 'graph:setData': return execSetDataFor(appState, action);
    case 'graph:replaceNode': return execReplaceNodeFor(appState, action);
    case 'graph:addEdge': return execAddEdgeFor(appState, action);
    case 'graph:removeEdge': return execRemoveEdgeFor(appState, action);
    case 'graph:connect': return execConnectFor(appState, action);
    case 'graph:reconnect': return execReconnectFor(appState, action);
    case 'graph:cutEdges': return execCutEdgesFor(appState, action);
    case 'params:setValue': return execSetParamValueFor(appState, action);
    case 'params:deleteValue': return execDeleteParamValueFor(appState, action);
    case 'scene:addFrame': return execAddFrameFor(appState, action);
    case 'scene:addLayer': return execAddLayerFor(appState, action);
    case 'scene:removeFrame': return execRemoveFrameFor(appState, action);
    case 'scene:addInstance': return execAddInstanceFor(appState, action);
    case 'scene:addExistingInstance': return execAddExistingInstanceFor(appState, action);
    case 'scene:dropLayer': return execDropLayerFor(appState, action, hooks.sceneRemoval);
    case 'scene:dropInstance': return execDropInstanceFor(appState, action);
    case 'scene:dropOwnedInstance': return execDropOwnedInstanceFor(appState, action, hooks.sceneRemoval);
    case 'scene:deleteNode': return execDeleteSceneNodeFor(appState, action, hooks.sceneRemoval);
    case 'scene:restoreSubtree': return execRestoreSceneSubtreeFor(appState, action);
    case 'scene:setFrameProperty': return execSetFramePropertyFor(appState, action);
    case 'scene:setInstanceProperty': return execSetInstancePropertyFor(appState, action);
    case 'scene:setNodeParamOverride': return execSetNodeParamOverrideFor(appState, action);
    case 'scene:rename': return execRenameSceneNodeFor(appState, action);
    case 'component:rename': return execRenameComponentFor(appState, action);
    case 'app:clear': return execClearFor(appState, hooks.clear);
    case 'component:restore': return execRestoreComponentFor(appState, action);
    case 'layer:restore': return execRestoreLayerDefFor(appState, action);
    case 'editor:restoreSession': return execRestoreEditorSessionFor(appState, action, hooks.editorSession);
    case 'library:create': return execCreateLibraryItemFor(appState, action);
    case 'library:save': return execSaveToLibraryFor(appState, action);
    case 'library:restore': return execRestoreLibraryItemFor(appState, action);
    case 'library:delete': return execDeleteLibraryItemFor(appState, action);
    case 'library:attach': return execAttachLibraryFor(appState, action);
    case 'library:detach': return execDetachLibraryFor(appState, action);
    case 'component:setSlot': return execSetComponentSlotFor(appState, action, hooks.componentSlot);
    case 'runtime:setGraph': return execSetRuntimeGraphFor(appState, action);
    case 'data:setOwner': return execSetDataOwnerFor(appState, action);
    case 'data:setSources': return execSetDataSourcesFor(appState, action);
    case 'assets:setShapes': return execSetShapesFor(appState, action);
    case 'runtime:setState': return execSetRuntimeStateFor(appState, action);
    case 'runtime:setModel': return execSetRuntimeModelFor(appState, action);
    case 'event:setSceneEvents': return execSetSceneEventsFor(appState, action);
    case 'data:setSceneContext': return execSetSceneDataContextFor(appState, action);
  }
}

function executeEditorCommandWithHooksFor(
  appState: AppState,
  cmd: EditorCommand,
  hooks: EditorCommandExecutorHooks = {},
): ActionExecution {
  const execution = executeActionFor(appState, cmd, hooks);
  if (!execution.changed) {
    return execution;
  }

  if (!isGraphStructureEdit(cmd)) {
    return execution;
  }

  const forkTransition = autoForkEditedBuiltInLibraryIfNeededFor(
    appState,
    hooks.autoForkBuiltInLibrary,
  );
  if (forkTransition) {
    return {
      ...execution,
      replayActions: [...forkTransition.replayActions, ...(execution.replayActions ?? [cmd])],
      undoActions: [...execution.undoActions, ...forkTransition.undoActions],
    };
  }

  const slotTransition = autoDetachEditedComponentSlotIfNeededFor(
    appState,
    hooks.autoDetachComponentSlot ?? editorAutoDetachComponentSlotHooks,
  );
  if (!slotTransition) {
    return execution;
  }

  return {
    ...execution,
    replayActions: [...(execution.replayActions ?? [cmd]), slotTransition.replayAction],
    undoActions: [...execution.undoActions, slotTransition.undoAction],
  };
}

export function createEditorCommandExecutor(
  hooks: EditorCommandExecutorHooks = {},
): EditorCommandExecutor {
  return {
    executeCommand(appState, cmd) {
      return executeEditorCommandWithHooksFor(appState, cmd, hooks);
    },
    executeInternalAction(appState, action) {
      return executeActionFor(appState, action, hooks);
    },
  };
}
